import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Globe, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ClientRegister = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Preset values come from the query string (CTA from /offerte or /factuur, or admin invite)
  const presetName = searchParams.get('name') || '';
  const presetEmail = searchParams.get('email') || '';
  const presetPhone = searchParams.get('phone') || '';
  const presetAddress = searchParams.get('address') || '';
  const presetCompany = searchParams.get('company') || '';

  const [formData, setFormData] = useState({
    name: presetName,
    email: presetEmail,
    password: '',
    password_confirm: '',
    phone: presetPhone,
    address: presetAddress,
    company_name: presetCompany,
  });
  const [loading, setLoading] = useState(false);
  const addressLocked = Boolean(presetAddress);

  // Keep form in sync if the user lands with empty params first then params arrive late (rare)
  useEffect(() => {
    setFormData((f) => ({
      ...f,
      name: f.name || presetName,
      email: f.email || presetEmail,
      phone: f.phone || presetPhone,
      address: presetAddress || f.address,
      company_name: f.company_name || presetCompany,
    }));
  }, [presetName, presetEmail, presetPhone, presetAddress, presetCompany]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'nl' ? 'en' : 'nl';
    i18n.changeLanguage(newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < 6) {
      toast.error(i18n.language === 'nl' ? 'Wachtwoord moet minimaal 6 tekens zijn' : 'Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.password_confirm) {
      toast.error(i18n.language === 'nl' ? 'Wachtwoorden komen niet overeen' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const { password_confirm, ...payload } = formData;
      await axios.post(`${BACKEND_URL}/api/client/register`, payload);
      toast.success(t('registerSuccess'));
      navigate('/client');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already registered')) {
        toast.error(t('emailExists'));
      } else {
        toast.error(t('error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-[#0d3d3d]/90 border-white/20 backdrop-blur">
        <CardHeader className="space-y-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-3xl font-bold text-white">{t('register')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-white hover:bg-white/10">
              <Globe className="w-4 h-4 mr-2" />
              {i18n.language === 'nl' ? 'EN' : 'NL'}
            </Button>
          </div>
          <p className="text-white/70">{t('welcome')}</p>
          {addressLocked && (
            <p className="text-xs text-[#3da5a5] bg-[#3da5a5]/10 px-3 py-2 rounded">
              {i18n.language === 'nl'
                ? '✓ Uw gegevens zijn al voor u ingevuld. U hoeft alleen een wachtwoord te kiezen en eventueel uw gegevens te bewerken.'
                : '✓ Your details are pre-filled. You only need to choose a password.'}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">{t('name')} *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">{t('email')} *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">{t('password')} *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength="6"
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">
                  {i18n.language === 'nl' ? 'Wachtwoord bevestigen' : 'Confirm password'} *
                </Label>
                <Input
                  type="password"
                  value={formData.password_confirm}
                  onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                  required
                  minLength="6"
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="register-password-confirm-input"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">{t('phone')}</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/10 border-white/30 text-white"
                  placeholder="+31 6 12345678"
                  data-testid="register-phone-input"
                />
              </div>
              <div className="md:col-span-1">
                <Label className="text-white mb-2 block flex items-center gap-1">
                  {t('address')} {addressLocked && <Lock className="w-3 h-3 text-[#3da5a5]" />}
                </Label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => !addressLocked && setFormData({ ...formData, address: e.target.value })}
                  readOnly={addressLocked}
                  className={`border-white/30 text-white ${addressLocked ? 'bg-white/5 cursor-not-allowed text-white/60' : 'bg-white/10'}`}
                  data-testid="register-address-input"
                  title={addressLocked ? (i18n.language === 'nl' ? 'Dit adres is door ons al ingesteld' : 'This address is preset by us') : ''}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-white mb-2 block">{t('companyName')}</Label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="register-company-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-6 text-lg font-semibold"
              data-testid="register-submit-btn"
            >
              {loading ? '...' : t('registerButton')}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-white/70 text-sm">
              {t('haveAccount')}{' '}
              <Link to="/client" className="text-[#3da5a5] hover:text-[#2d8585] font-semibold">
                {t('loginNow')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientRegister;
